/**
 * Показатели карточек: столбцы, состояния данных и подписи выводов.
 *
 * «Эффективность» вкладкой не становится: вкладки списка — это девять
 * взаимоисключающих состояний карточки, а карточка не бывает «в состоянии
 * эффективности». Показатели входят столбцами, уже существующей сортировкой
 * «Больше всего теряем» и двумя окнами.
 *
 * Модуль ничего не импортирует из `lib.ts`: наоборот, каталог столбцов
 * дописывает отсюда, и обратная зависимость замкнула бы круг.
 */

import type { BadgeVariant } from '../../components/ui'
import type {
  SeoAnalyticsOverview,
  SeoConfidence,
  SeoMetricsImportReport,
  SeoMetricsState,
  SeoPotential,
  SeoPotentialItem,
} from '../seo/api'
import type { SeoCardsColumnDef, SeoCardsColumnKey } from './types'

/** Ключи столбцов показателей — по ним страница понимает, что их просили. */
export const METRIC_COLUMN_KEYS: readonly SeoCardsColumnKey[] = [
  'impressions',
  'clicks',
  'ctr',
  'orders',
  'orderRate',
  'upside',
  'weakStep',
  'confidence',
]

/**
 * Определения столбцов показателей. Скрыты по умолчанию: они стоят запроса
 * к аналитике, и платить за него при обычной правке текста незачем.
 */
export const METRIC_COLUMNS: readonly SeoCardsColumnDef[] = [
  { key: 'impressions', label: 'Показы', align: 'right' },
  { key: 'clicks', label: 'Переходы', align: 'right' },
  { key: 'ctr', label: 'Переходы к показам', align: 'right' },
  { key: 'orders', label: 'Заказы', align: 'right' },
  { key: 'orderRate', label: 'Заказы к переходам', align: 'right' },
  { key: 'upside', label: 'Недобор заказов', align: 'right' },
  { key: 'weakStep', label: 'Где теряем' },
  { key: 'confidence', label: 'Уверенность' },
]

/** Включён ли хоть один столбец показателей: до этого их никто не просил. */
export function metricColumnsRequested(visible: Set<SeoCardsColumnKey>): boolean {
  return METRIC_COLUMN_KEYS.some((key) => visible.has(key))
}

/** Целое число по-русски; отсутствие значения — прочерк, а не ноль. */
export function metricCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : Math.round(value).toLocaleString('ru-RU')
}

/** Доля процентами; отсутствие значения — прочерк. */
export function metricPercent(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${(value * 100).toFixed(2)} %`
}

/** Изменение против базового варианта, со знаком. */
export function metricChange(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)} %`
}

export interface MetricsPeriod {
  from: string
  to: string
}

/** Период по умолчанию — последние 28 дней, как и на сервере. */
export function defaultMetricsPeriod(today: Date = new Date()): MetricsPeriod {
  const from = new Date(today)
  from.setDate(from.getDate() - 27)
  return { from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) }
}

/**
 * Строка покрытия данных. Без неё пустая очередь неотличима от «данных нет» —
 * это главный смысл окна показателей.
 */
export function coverageLine(state: SeoMetricsState | null): string {
  if (!state) return 'Состояние показателей кабинета неизвестно: запрос не выполнен.'
  const { coverage } = state
  if (!coverage.days || !coverage.first_date || !coverage.last_date) {
    return 'Показатели этого кабинета ещё ни разу не загружались.'
  }
  const sources = coverage.sources.length ? coverage.sources.join(', ') : 'источник не указан'
  return (
    `Данные за ${coverage.first_date} — ${coverage.last_date}: ${coverage.days} дн., `
    + `${coverage.articles.toLocaleString('ru-RU')} карточек. Источники: ${sources}.`
  )
}

/** Отдельная строка про последнюю загрузку; её не было — null. */
export function lastImportLine(state: SeoMetricsState | null): string | null {
  const record = state?.imports?.[0]
  if (!record) return null
  const when = new Date(record.created_at)
  const stamp = Number.isNaN(when.getTime()) ? record.created_at : when.toLocaleString('ru-RU')
  return `Последняя загрузка: ${stamp} (${record.source}), записано ${record.rows_written.toLocaleString('ru-RU')} строк.`
}

export type ToastTone = 'success' | 'info' | 'warning' | 'error'

/**
 * Сводка загрузки файлом. Пропущенные строки называются отдельно: «загружено
 * 900» без «пропущено 100» читается как «загрузилось всё».
 */
export function importSummary(report: SeoMetricsImportReport): { text: string; tone: ToastTone } {
  if (!report.written) {
    return {
      text: `Ни одна строка не записана: получено ${report.received.toLocaleString('ru-RU')}, пропущено ${report.skipped.toLocaleString('ru-RU')}`,
      tone: 'error',
    }
  }
  const skipped = report.skipped ? `, пропущено ${report.skipped.toLocaleString('ru-RU')}` : ''
  return {
    text: `Загружено строк: ${report.written.toLocaleString('ru-RU')}${skipped}`,
    tone: report.skipped ? 'warning' : 'success',
  }
}

/**
 * Сводка обращения к кабинету. Частичный успех отличается от отказа: при
 * `partial` часть периода получена, при `failed` — ничего.
 */
export function pullSummary(report: SeoMetricsImportReport): { text: string; tone: ToastTone } {
  const base =
    `Из кабинета получено строк: ${report.received.toLocaleString('ru-RU')}, `
    + `записано ${report.written.toLocaleString('ru-RU')}`
  if (report.status === 'failed') return { text: `Кабинет не отдал показатели. ${base}`, tone: 'error' }
  if (report.status === 'partial') return { text: `${base}. Часть периода не получена`, tone: 'warning' }
  return { text: base, tone: 'success' }
}

/** Замечания разбора показываются отдельно от чисел: это разные новости. */
export function importNotes(report: SeoMetricsImportReport): string[] {
  return report.notes?.filter((note) => !!note) ?? []
}

export type WeakStepTone = 'issue' | 'ok' | 'unknown'

export interface WeakStepPresentation {
  tone: WeakStepTone
  label: string
  variant: BadgeVariant
  explanation: string
}

const WEAK_STEP_LABELS: Record<string, string> = {
  ctr: 'Переходы',
  cart: 'Корзина',
  order: 'Заказы',
}

/**
 * Три состояния «Где теряем», и все три обязаны различаться.
 *
 * «На уровне категории» — это вывод, и делать его можно только когда недобор
 * посчитан. Пока сравнивать не с чем, вывода нет: зелёный значок здесь был бы
 * враньём, а не оптимизмом.
 */
export function weakStepPresentation(potential: SeoPotential | null | undefined): WeakStepPresentation {
  if (!potential) {
    return {
      tone: 'unknown',
      label: 'Вывода пока нет',
      variant: 'gray',
      explanation: 'Показателей по этой карточке за период нет.',
    }
  }
  if (potential.weak_step) {
    return {
      tone: 'issue',
      label: WEAK_STEP_LABELS[potential.weak_step] || potential.weak_step,
      variant: 'yellow',
      explanation: potential.explanation || '',
    }
  }
  if (potential.upside_orders === null || potential.upside_orders === undefined) {
    return {
      tone: 'unknown',
      label: 'Вывода пока нет',
      variant: 'gray',
      explanation: potential.explanation || 'Недобор заказов не посчитан: сравнивать не с чем.',
    }
  }
  return {
    tone: 'ok',
    label: 'На уровне категории',
    variant: 'green',
    explanation: potential.explanation || '',
  }
}

/** Насколько выводу можно верить: это про объём данных, а не про качество текста. */
export function confidencePresentation(confidence: SeoConfidence | null | undefined): {
  label: string
  variant: BadgeVariant
} {
  if (confidence === 'high') return { label: 'Данных достаточно', variant: 'green' }
  if (confidence === 'medium') return { label: 'Данных немного', variant: 'yellow' }
  if (confidence === 'low') return { label: 'Данных мало', variant: 'gray' }
  return { label: 'Нет данных', variant: 'gray' }
}

/** Надёжность ориентира категории: по нему сравнивается каждая карточка. */
export function benchmarkReliability(reliable: boolean): { label: string; variant: BadgeVariant } {
  return reliable ? { label: 'Надёжен', variant: 'green' } : { label: 'Мало данных', variant: 'gray' }
}

/** Показатели по артикулу: очередь потенциала — единственный их источник в списке. */
export function indexPotential(items: SeoPotentialItem[] | null | undefined): Map<string, SeoPotentialItem> {
  return new Map((items || []).map((item) => [item.article, item]))
}

/**
 * Почему столбцы показателей пусты. null — данные есть, полосу показывать не
 * нужно. Столбцы не показываются молча пустыми: молчание читается как поломка.
 */
export function metricsEmptyReason(
  loading: boolean,
  queueEmptyReason: string | null | undefined,
  loaded: number,
): string | null {
  if (loading) return null
  if (loaded > 0) return null
  return queueEmptyReason || 'За выбранный период показателей по карточкам нет.'
}

/** Сводка окна: воронка кабинета пятью числами. */
export function overviewTiles(overview: SeoAnalyticsOverview | null): Array<{ label: string; value: string }> {
  return [
    { label: 'Показы', value: metricCount(overview?.totals.impressions) },
    { label: 'Переходы', value: metricCount(overview?.totals.clicks) },
    { label: 'Переходы к показам', value: metricPercent(overview?.rates.ctr) },
    { label: 'Заказы', value: metricCount(overview?.totals.orders) },
    { label: 'Заказы к переходам', value: metricPercent(overview?.rates.order_rate) },
  ]
}
